<template>
  <div class="filter-section">
    <div class="filter-row">
      <div class="filter-group">
        <label>{{ $t("logwork.filters.scope") }}</label>
        <a-select
          v-model="localScope"
          :options="scopeOptions"
          @change="handleScopeChange"
          class="scope-select"
        />
      </div>

      <div class="filter-group" v-if="localScope === 'Project'">
        <label>{{ $t("logwork.filters.project") }}</label>
        <a-select
          mode="multiple"
          v-model="localSelectedProjects"
          :options="projectOptions"
          :placeholder="$t('logwork.filters.project_placeholder')"
          :loading="loadingProjects"
          :maxTagCount="isAllProjectsSelected ? 0 : undefined"
          :maxTagPlaceholder="isAllProjectsSelected ? allProjectsTag : undefined"
          class="project-select"
          @change="emitProjects"
        />
      </div>

      <div class="filter-group" v-if="localScope === 'Member'">
        <label>{{ $t("logwork.filters.member") }}</label>
        <a-select
          mode="multiple"
          v-model="localSelectedMembers"
          :options="usedMemberOptions"
          :placeholder="$t('logwork.filters.member_placeholder')"
          :loading="loadingMembers"
          :maxTagCount="isAllMembersSelected ? 0 : undefined"
          :maxTagPlaceholder="isAllMembersSelected ? allMembersTag : undefined"
          class="member-select"
          @change="emitMembers"
        />
      </div>

      <div class="filter-group">
        <label>{{ $t("logwork.filters.group_by") }}</label>
        <a-select
          v-model="localGroupBy"
          :options="visibleGroupByOptions"
          class="group-select"
          @change="emitGroupBy"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LogworkService from "../../api/logwork";

export default {
  name: "FilterSection",
  props: {
    scope: { type: String, default: "Project" },
    scopeOptions: { type: Array, default: () => [] },
    selectedProjects: { type: Array, default: () => [] },
    selectedMembers: { type: Array, default: () => [] },
    groupBy: { type: String, default: "By Member" },
    groupByOptions: { type: Array, default: () => [] },
    visibleGroupByOptions: { type: Array, default: () => [] },
    // Optional member options coming from parent (preferred)
    memberOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      localScope: this.scope,
      localSelectedProjects: this.selectedProjects.slice(),
      localSelectedMembers: this.selectedMembers.slice(),
      localGroupBy: this.groupBy,
      projects: [],
      members: [],
      loadingProjects: false,
      loadingMembers: false
    };
  },
  computed: {
    projectOptions() {
      return this.projects.map(project => ({
        value: project.id,
        label: project.name
      }));
    },
    projectOptionValues() {
      return (this.projectOptions || []).map(o => o.value);
    },
    isAllProjectsSelected() {
      const optionValues = this.projectOptionValues || [];
      const selected = Array.isArray(this.localSelectedProjects)
        ? this.localSelectedProjects
        : [];

      if (optionValues.length === 0) return false;
      
      const selectedSet = new Set(selected);
      return optionValues.every(v => selectedSet.has(v));
    },
    allProjectsTag() {
      return this.$t("logwork.filters.all_project") || "All project";
    },
    memberOptionValues() {
      return (this.usedMemberOptions || []).map(o => o.value);
    },
    isAllMembersSelected() {
      const optionValues = this.memberOptionValues || [];
      const selected = Array.isArray(this.localSelectedMembers)
        ? this.localSelectedMembers
        : [];
      if (optionValues.length === 0) return false;
      const selectedSet = new Set(selected);
      return optionValues.every(v => selectedSet.has(v));
    },
    allMembersTag() {
      return this.$t("logwork.filters.all_member") || "Tất cả thành viên";
    },
    // Prefer parent-supplied memberOptions if provided, otherwise fall back to local members
    usedMemberOptions() {
      if (this.memberOptions && Array.isArray(this.memberOptions) && this.memberOptions.length > 0) {
        return this.memberOptions;
      }
      return this.members.map(member => ({
        value: member.id,
        label: member.employee_name || member.name
      }));
    }
  },
  watch: {
    scope(val) {
      this.localScope = val;
    },
    selectedProjects(val) {
      this.localSelectedProjects = val.slice();
      // Refetch members when selected projects change
      if (this.localScope === "Member" && val.length > 0) {
        this.fetchMembers(val);
      }
    },
    selectedMembers(val) {
      this.localSelectedMembers = val.slice();
    },
    groupBy(val) {
      this.localGroupBy = val;
    }
  },
  async mounted() {
    // Fetch projects on mount
    await this.fetchProjects();

    // Fetch members if scope is Member
    if (this.localScope === "Member") {
      await this.fetchMembers();
    }
  },
  methods: {
    async handleScopeChange(val) {
      this.$emit("update:scope", val);

      const defaultGroupBy = val === "Project" ? "By Member" : "By Project";
      this.localGroupBy = defaultGroupBy;
      this.emitGroupBy(defaultGroupBy);

      // Fetch members when switching to Member scope
      if (val === "Member") {
        await this.fetchMembers();
      }

      this.$emit("scope-change", val);
    },
    emitProjects(val) {
      this.$emit("update:selectedProjects", val);
      this.$emit("projects-change", val);
    },
    emitMembers(val) {
      this.$emit("update:selectedMembers", val);
      this.$emit("members-change", val);
    },
    emitGroupBy(val) {
      this.$emit("update:groupBy", val);
      this.$emit("groupby-change", val);
    },

    async fetchProjects() {
      try {
        this.loadingProjects = true;
        const response = await LogworkService.getProjects();
        this.projects = response.data?.data || [];
      } catch (error) {
        console.error("Failed to fetch projects:", error);
        this.$message.error("Failed to load projects");
      } finally {
        this.loadingProjects = false;
      }
    },

    async fetchMembers(projectIds = null) {
      try {
        this.loadingMembers = true;
        const params = projectIds ? { project_ids: projectIds.join(",") } : {};
        const response = await LogworkService.getMembers(params);
        this.members = response.data?.data || [];
      } catch (error) {
        console.error("Failed to fetch members:", error);
        this.$message.error("Failed to load members");
      } finally {
        this.loadingMembers = false;
      }
    },

    async refreshData() {
      await this.fetchProjects();
      if (this.localScope === "Member") {
        await this.fetchMembers(
          this.localSelectedProjects.length > 0
            ? this.localSelectedProjects
            : null
        );
      }
    }
  }
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 20px;
}
.filter-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-start;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 200px;

  label {
    margin: 0;
  }
}
.scope-select {
  min-width: 140px;
}
.project-select,
.member-select {
  max-width: 300px;
}
.group-select {
  min-width: 150px;
}
</style>
